/**
 * src/pages/AssistantHelp.tsx
 * アシスタント向けMangaOS操作マニュアル
 */

import { useState } from 'react'

// ----------------------------------------------------------------
// セクションデータ定義
// ----------------------------------------------------------------
type Section = {
  id: string
  title: string
  content: React.ReactNode
}

const sections: Section[] = [
  {
    id: 'intro',
    title: 'はじめに',
    content: (
      <>
        <p>MangaOSは、アシスタントさんの日々の業務をサポートするポータルサイトです。</p>
        <p>このヘルプページでは、MangaOSでできること・操作手順をまとめています。
        わからないことがあったときに、いつでも確認してください。</p>
        <div className="help-note">
          <strong>画面左のメニュー（スマホは左上のハンバーガーメニュー☰）</strong>から各機能に移動できます。
        </div>
      </>
    ),
  },
  {
    id: 'overview',
    title: 'ログイン後にできること',
    content: (
      <>
        <p>MangaOSにログインすると、以下の機能が使えます。</p>
        <table className="help-table">
          <thead>
            <tr><th>メニュー</th><th>できること</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>日報</strong></td><td>日々の作業内容を記録・確認する</td></tr>
            <tr><td><strong>勤怠入力</strong></td><td>出退勤時刻や勤務時間を入力する</td></tr>
            <tr><td><strong>シフト希望</strong></td><td>翌月の出勤希望日をカレンダーで選ぶ</td></tr>
            <tr><td><strong>確定シフト</strong></td><td>確定した自分のシフトを確認する</td></tr>
            <tr><td><strong>給与申告</strong></td><td>月次の給与申告フォームを記入・提出する</td></tr>
            <tr><td><strong>作品・話数</strong></td><td>担当作品の話数・締め切りを確認する</td></tr>
          </tbody>
        </table>
        <p>右上（PC）またはメニューの「⚙ アカウント設定」から、メールアドレスやパスワードを変更できます。</p>
      </>
    ),
  },
  {
    id: 'reports',
    title: '日報を書く・確認する',
    content: (
      <>
        <p>その日の作業内容を記録する機能です。毎日の作業後に提出してください。</p>

        <h4>日報を新規作成する</h4>
        <ol>
          <li>左メニューの「<strong>日報</strong>」を選ぶ</li>
          <li>右上の「<strong>＋ 日報を書く</strong>」ボタンをクリック</li>
          <li>「日報入力」フォームに以下を入力する
            <ul>
              <li><strong>日付</strong>：作業した日付（デフォルトは今日）</li>
              <li><strong>作品名</strong>：作業した作品名をドロップダウンから選ぶ</li>
              <li><strong>話数</strong>：対象の話数番号</li>
              <li><strong>工程</strong>：ネーム / ネーム修正 / 下書き / ペン入れ / 背景 / 仕上げ / 修正 / その他 から選ぶ</li>
              <li><strong>完了ページ数</strong>：その日に完了したページ数</li>
              <li><strong>作業内容</strong>：作業の詳細メモ（例：教室背景まで完了、次回は廊下シーンから）</li>
              <li><strong>引継ぎ・連絡事項</strong>：担当者への申し送り（「特になし」でもOK）</li>
              <li><strong>作業画像（任意）</strong>：参考画像や作業スクリーンショット</li>
            </ul>
          </li>
          <li>「<strong>保存</strong>」ボタンをクリック</li>
        </ol>

        <div className="help-note">
          <strong>複数作品の記録：</strong>「＋ 作業を追加」ボタンで、1日に複数の作品・工程を記録できます。作品ごと・話数ごとに作業の行を分けて入力してください。
        </div>

        <h4>カイカイキキ業務の場合</h4>
        <p>作品名ドロップダウンで「カイカイキキ業務」を選ぶと、作業内容メモ欄のみが表示されます。話数・工程・ページ数の入力は不要です。</p>

        <h4>作業データ保存先を入力する</h4>
        <p>各作業行の「<strong>作業データ保存先（任意）</strong>」欄に、その作業のデータを保存したフォルダのパスを入力できます。複数作品を記録する場合は、作品ごとにそれぞれの保存先を入力してください。</p>
        <p>Windows・Macでパスの書き方が違っても、変換せずそのまま入力して問題ありません。</p>
        <p><strong>Windows例：</strong></p>
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: 12, background: 'var(--paper-2)',
          border: '1px solid var(--paper-3)', borderRadius: 8, padding: '8px 12px', margin: '6px 0',
          wordBreak: 'break-all', overflowWrap: 'anywhere', maxWidth: '100%',
        }}>
          {String.raw`\\192.168.40.197\disk1\漫画\メンコリバーサーカケル\12話`}
        </div>
        <p><strong>Mac例：</strong></p>
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: 12, background: 'var(--paper-2)',
          border: '1px solid var(--paper-3)', borderRadius: 8, padding: '8px 12px', margin: '6px 0',
          wordBreak: 'break-all', overflowWrap: 'anywhere', maxWidth: '100%',
        }}>
          smb://192.168.40.197/disk1/漫画/メンコリバーサーカケル/12話
        </div>
        <div className="help-note">
          保存先が分かりにくい場合は、作業画像欄に作業フォルダを開いた状態のスクリーンショットを添付するのも有効です。ただしスクリーンショットはあくまで補助なので、できるだけパスも文字で入力してください。未入力のままでも日報の提出はできます。
        </div>

        <h4>作業画像を添付する</h4>
        <p>日報入力フォームの「作業画像（任意 / 複数可）」エリアで、画像を3種類の方法で添付できます。</p>
        <ol>
          <li><strong>「＋ 追加」をクリック：</strong>ファイル選択ダイアログからPCやスマホの画像ファイルを選ぶ</li>
          <li><strong>ドラッグ＆ドロップ：</strong>画像ファイルをフォーム上にドロップする（PC）</li>
          <li><strong>Ctrl+V で貼り付け：</strong>クリップボードにコピーした画像を直接ペーストする（PC）</li>
        </ol>
        <div className="help-note">
          対応形式はJPEG・PNG・WebP・GIFなどです（各5MBまで）。アップロード後はサムネイルをクリックすると拡大表示されます。
        </div>
        <div className="help-note">
          <strong>CLIP STUDIO Paint の .clip ファイルも添付できます。</strong>
          .clipファイルをドラッグ＆ドロップまたは「＋ 追加」から選択すると、ファイル内に保存されているサムネイル画像を自動で抽出して添付します（.clip本体はアップロードされません）。
          抽出できなかった場合は、CLIP STUDIO PaintからPNG/JPEGに書き出してから添付してください。
        </div>

        <hr className="help-sep" />

        <h4>既存の日報を編集・削除する</h4>
        <ol>
          <li>日報一覧から対象の日報をクリックして詳細モーダルを開く</li>
          <li>「<strong>編集</strong>」または「<strong>削除</strong>」ボタンを使う</li>
        </ol>

        <div className="help-caution">
          同じ日付の日報が既に存在する場合、「日報を上書きしますか？」という確認ダイアログが表示されます。上書きすると元の内容は復元できません。
        </div>

        <hr className="help-sep" />

        <h4>日報一覧の見方</h4>
        <ul>
          <li>日付・作品名・工程・ページ数が一覧表示されます</li>
          <li>添付画像がある場合はサムネイルが表示されます</li>
          <li>詳細モーダルでは、引継ぎ事項や全ての作業明細を確認できます</li>
        </ul>

        <hr className="help-sep" />

        <h4>日報リマインダー通知</h4>
        <p>日報画面の「通知設定」から、日報を書き忘れた日にブラウザ通知を受け取る設定ができます。</p>
        <ol>
          <li>「通知を有効にする」をクリックする</li>
          <li>ブラウザから通知の許可を求められたら「許可」を選ぶ</li>
          <li>当日の日報がない場合、夜に通知が届きます</li>
        </ol>
        <div className="help-note">
          iOSのSafariなど一部のブラウザでは通知機能が使えない場合があります。
        </div>
      </>
    ),
  },
  {
    id: 'attendance',
    title: '勤怠を入力する',
    content: (
      <>
        <p>出退勤時刻や勤務時間を月ごとに記録する機能です。</p>

        <h4>勤怠を入力する手順</h4>
        <ol>
          <li>左メニューの「<strong>勤怠入力</strong>」を選ぶ</li>
          <li>対象の月を「＜ ＞」ボタンで切り替える</li>
          <li>「<strong>＋ 勤怠を追加</strong>」ボタンをクリックする</li>
          <li>「勤怠を入力」フォームに以下を入力する
            <ul>
              <li><strong>出勤日</strong>（必須）：出勤した日</li>
              <li><strong>勤務区分</strong>：「フルタイム（日給）」または「時間勤務・時短勤務」を選ぶ</li>
              <li><strong>出勤時刻 / 退勤時刻</strong>：出退勤の時刻を入力する</li>
              <li><strong>残業時間（分）</strong>：フルタイムの場合、出退勤時刻を入力すると自動計算される（読み取り専用）</li>
              <li><strong>交通費（円）</strong>：その日の交通費</li>
              <li><strong>時間勤務・時短勤務の勤務時間（分）</strong>：「時間勤務・時短勤務」選択時、出退勤時刻と休憩時間を入力すると自動計算される</li>
              <li><strong>休憩時間</strong>：「時間勤務・時短勤務」選択時に選ぶ</li>
              <li><strong>食事支給</strong>：あり / なし を選ぶ</li>
              <li><strong>備考</strong>：特記事項があれば入力</li>
            </ul>
          </li>
          <li>「<strong>保存</strong>」ボタンをクリック</li>
        </ol>

        <h4>残業・勤務時間の自動計算</h4>
        <p>残業時間・勤務時間はボタンを押して計算するのではなく、出勤時刻・退勤時刻・休憩時間を入力すると<strong>その場で自動的に計算</strong>されます。</p>
        <ul>
          <li>
            <strong>フルタイム（日給）の場合：</strong>実働時間（退勤時刻 − 出勤時刻 − 休憩90分）が、通常の勤務基準（510分 / 8.5時間）を超えた分だけ残業として自動計算されます。「20:00を過ぎたら必ず残業になる」わけではなく、あくまで実働時間の基準です。
          </li>
          <li>
            <strong>時間勤務・時短勤務の場合：</strong>残業は計上されません。出退勤時刻と休憩時間から勤務時間（分）のみが自動計算されます。
          </li>
        </ul>
        <div className="help-note">
          例：12:00〜20:50勤務（休憩90分）でも、実働時間が通常勤務基準を超えなければ残業は0分になります。実働時間 = 8時間50分 − 休憩90分 = 7時間20分（440分）で、510分の基準に達していないためです。
        </div>
        <div className="help-note">
          入力を修正した場合も、保存前に画面上の残業時間・勤務時間の表示を確認してから保存してください。保存時はサーバー側でも同じ式で必ず再計算されるため、手入力で残業時間だけを変えることはできません。
        </div>

        <h4>勤怠を修正・削除する</h4>
        <p>入力済みの勤怠行の鉛筆アイコンをクリックすると「勤怠を修正」モーダルが開きます。削除は「この勤怠入力を削除」リンクから行います。</p>

        <div className="help-note">
          勤怠入力・修正のモーダルは、入力中の内容が誤って消えないよう背景（枠外）クリックでは閉じません。閉じる場合は「✕」またはキャンセルのボタンを使ってください。
        </div>

        <div className="help-caution">
          締め済みの月は入力・修正ができません（閲覧のみ）。締め処理が終わった後の修正は管理者に連絡してください。
        </div>
      </>
    ),
  },
  {
    id: 'shift-request',
    title: 'シフト希望を出す',
    content: (
      <>
        <p>翌月の出勤希望日を提出する機能です。毎月25日ごろを目安に提出してください。</p>

        <h4>シフト希望の提出手順</h4>
        <ol>
          <li>左メニューの「<strong>シフト希望</strong>」を選ぶ</li>
          <li>翌月のカレンダーが表示される（月は「＜ ＞」で切り替え可能）</li>
          <li>出勤できる日付をカレンダー上でクリックして選ぶ（もう一度クリックで解除）</li>
          <li>「<strong>提出</strong>」ボタンをクリックする</li>
        </ol>

        <div className="help-note">
          毎月25日には「今月はシフト希望の提出日です」というバナーが表示されます。
        </div>

        <h4>提出後の修正</h4>
        <p>提出済みのシフト希望は、締め切り前であれば再編集して再提出できます。カレンダーを修正してもう一度「提出」ボタンをクリックしてください。</p>

        <div className="help-caution">
          締め切り後または月次締め後は編集できなくなります（読み取り専用表示）。
        </div>
      </>
    ),
  },
  {
    id: 'shifts',
    title: '確定シフトを確認する',
    content: (
      <>
        <p>管理者が確定した自分のシフトを確認できます。</p>

        <h4>確定シフトの見方</h4>
        <ol>
          <li>左メニューの「<strong>確定シフト</strong>」を選ぶ</li>
          <li>月を「＜ ＞」ボタンで切り替えて確認する</li>
        </ol>

        <h4>表示される情報</h4>
        <ul>
          <li>カレンダー形式で確定した出勤日がチップ表示されます</li>
          <li>月の出勤日数と概算給与が表示されます</li>
          <li>管理者からのメモがある場合は、カレンダーの下に表示されます</li>
        </ul>

        <div className="help-note">
          確定シフトは閲覧のみです。変更は管理者に連絡してください。
        </div>
      </>
    ),
  },
  {
    id: 'payroll',
    title: '給与申告をする',
    content: (
      <>
        <p>月次の給与申告フォームを記入・提出する機能です。月末に記入してください。</p>

        <h4>勤怠データから自動入力する</h4>
        <p>「<strong>📋 勤怠データから自動入力</strong>」ボタンをクリックすると、登録済みの勤怠記録をもとに各項目の初期値が自動で入力されます。入力後に内容を確認・修正してから提出してください。</p>

        <h4>フォームの各項目</h4>

        <p><strong>① フルタイム勤務日</strong></p>
        <ul>
          <li>カレンダーから出勤日をクリックして選ぶ（もう一度クリックで解除）</li>
        </ul>

        <p><strong>② フルタイム以外の勤務</strong></p>
        <ul>
          <li>早退・時短・一部時間だけの勤務を記入する</li>
          <li>「<strong>＋ 時間勤務を追加</strong>」ボタンで日付・時間（分）・備考を行単位で追加できる</li>
        </ul>

        <p><strong>③ 残業</strong></p>
        <ul>
          <li><strong>残業時間合計（分）</strong>：月の残業時間の合計を分で入力する</li>
          <li>「<strong>＋ 残業日を追加</strong>」ボタンで日ごとの内訳を追加できる（内訳がある場合は合計が自動計算）</li>
        </ul>

        <p><strong>④ 交通費</strong></p>
        <ul>
          <li><strong>通常の交通費（日額）：</strong>普段の1日あたりの交通費を入力して「保存」を押しておくと、勤務日追加時に自動入力されます</li>
          <li><strong>交通費合計（円）</strong>：月の交通費合計を入力する</li>
          <li><strong>内訳メモ（任意）</strong>：交通費の詳細があれば記入する</li>
        </ul>

        <p><strong>⑤ その他経費</strong></p>
        <ul>
          <li><strong>その他経費合計（円）</strong>：その他の経費を入力する</li>
          <li><strong>内訳メモ（任意）</strong>：経費の内訳があれば記入する</li>
        </ul>

        <p><strong>⑥ 備考</strong></p>
        <ul>
          <li>管理者への連絡事項があれば記入する</li>
        </ul>

        <h4>下書き保存と申告を提出</h4>
        <ul>
          <li><strong>下書き保存</strong>：内容を保存するが管理者には送信されない。後から修正できる</li>
          <li><strong>申告を提出</strong>：確認ダイアログが表示され、「この内容で提出する」をクリックすると管理者に送信される</li>
        </ul>

        <div className="help-caution">
          提出後は内容を直接修正できません。記入内容をよく確認してから提出してください。締め済み月も閲覧のみになります。
        </div>

        <h4>提出後に間違いがあった場合（修正依頼）</h4>
        <p>提出済みの場合、「給与申告は送信済みです。」というバナーが表示されます。間違いがあれば「<strong>修正依頼を送る</strong>」ボタンをクリックしてください。</p>
        <ol>
          <li>「修正依頼を送る」モーダルが開く</li>
          <li><strong>修正理由</strong>（必須）を入力する</li>
          <li>修正したい項目を変更する（変更内容プレビューが表示される）</li>
          <li>「<strong>修正依頼を送る</strong>」ボタンで管理者に送信する</li>
        </ol>
        <div className="help-note">
          送信した修正依頼は「修正依頼履歴」に一覧表示されます。管理者が対応すると「反映済み」または「対応済み」に変わります。
        </div>

        <hr className="help-sep" />

        <h4>入力途中でページを離れようとした場合</h4>
        <p>まだ保存・提出していない変更がある状態で、他のページに移動したりブラウザを閉じようとしたりすると、次の確認メッセージが表示されます。</p>
        <div className="help-note">
          「入力途中の内容があります。このまま離れると保存されません。移動してもよろしいですか？」
        </div>
        <p>何も変更していない場合や、下書き保存・提出が完了している場合はこの確認は表示されません。入力を中断する場合は、先に「<strong>下書き保存</strong>」しておくと安心です。</p>

        <h4>提出忘れのリマインドについて</h4>
        <p>対象月の給与申告がまだ提出されていない場合、管理者からリマインドメールが届くことがあります。心当たりがある場合は早めに申告してください。</p>
      </>
    ),
  },
  {
    id: 'works',
    title: '作品・話数を確認する',
    content: (
      <>
        <p>担当している作品の一覧と、各話数の進行状況・締め切りを確認できます。</p>

        <h4>作品一覧を見る</h4>
        <ol>
          <li>左メニューの「<strong>作品・話数</strong>」を選ぶ</li>
          <li>作品の一覧が表示される</li>
          <li>作品名をクリックすると話数の一覧が展開される</li>
        </ol>

        <h4>表示される情報</h4>
        <ul>
          <li>各話数のネーム / 提出 / 入稿 締め切り日</li>
          <li>締め切りまでの残り日数（近い場合は赤・黄色で警告表示）</li>
          <li>各話数の工程ごとの進捗ステータス</li>
        </ul>

        <div className="help-note">
          作品・話数の追加・編集は管理者のみが行えます。表示内容の誤りがある場合は管理者に連絡してください。
        </div>
      </>
    ),
  },
  {
    id: 'account',
    title: 'アカウント設定',
    content: (
      <>
        <p>メールアドレスとパスワードを変更できます。</p>

        <h4>アカウント設定を開く</h4>
        <ul>
          <li><strong>PC：</strong>右上のメールアドレスをクリック → 「アカウント設定」ページへ</li>
          <li><strong>スマホ：</strong>ハンバーガーメニュー（☰）→「⚙ アカウント設定」をタップ</li>
        </ul>

        <h4>メールアドレスを確認・変更する</h4>
        <p>メールアドレスは<strong>ログインID</strong>です。確認コードで、あなたのアドレスだと確かめてから登録します。</p>
        <ol>
          <li>アカウント設定画面の「<strong>メールアドレスを確認・変更する</strong>」を押して、「ログイン・セキュリティ」を開く</li>
          <li>画面の案内（STEP 1）に沿って、<strong>自分が受信できるメールアドレス</strong>とパスワードを入力し、「確認コードを送る」を押す</li>
          <li>メールに届いた6桁のコードを入力して「確認する」を押す</li>
        </ol>
        <p>今までと違うアドレスにした場合は、<strong>次回から、新しいアドレスと今までのパスワードでログイン</strong>します（新しいアドレスをメモしてください。他の端末のログインは外れます）。</p>

        <h4>パスワードを変更する</h4>
        <ol>
          <li>アカウント設定画面の「現在のパスワード」と「新しいパスワード」を入力する</li>
          <li>「<strong>パスワードを変更する</strong>」ボタンをクリック</li>
        </ol>

        <div className="help-note">
          初回ログイン時はパスワードの変更が求められる場合があります。案内に従って設定してください。
        </div>

        <hr className="help-sep" />

        <h4>仮メールアドレスの案内が出る場合</h4>
        <p>登録メールアドレスがまだ<code>@mangaos.test</code>や<code>@assistant.local</code>で終わる仮メールアドレスのままだと、画面上部に黄色い案内バナーが表示されます。</p>
        <ul>
          <li>確定シフトや給与関連のお知らせは、このアドレスには届きません</li>
          <li>バナーの「<strong>メールアドレスを登録・確認する</strong>」ボタン、または「アカウント設定 → メールアドレスを確認・変更する」から開く「ログイン・セキュリティ」で、本人が実際に使っているメールアドレスを確認コードで登録してください（登録したアドレスが次回からのログインIDになります）</li>
          <li>バナーは×ボタンでそのログイン中だけ非表示にできますが、次回ログイン時にはまた表示されます</li>
        </ul>
        <div className="help-caution">
          仮メールアドレスのままだと、確定シフトの通知や給与に関する重要なお知らせが届かない可能性があります。できるだけ早めに本人のメールアドレスへの変更をお願いします。
        </div>
      </>
    ),
  },
  {
    id: 'trouble',
    title: '困ったとき',
    content: (
      <>
        <h4>ログインできない</h4>
        <ul>
          <li>メールアドレス・パスワードを確認してください</li>
          <li>パスワードを忘れた場合は管理者に連絡してください</li>
        </ul>

        <h4>入力した内容が保存されない</h4>
        <ul>
          <li>ページを再読み込みして、再度入力してみてください</li>
          <li>締め済みの月や提出済みの申告は編集できません</li>
        </ul>

        <h4>シフトや給与の内容に誤りがある</h4>
        <ul>
          <li>確定シフトの修正は管理者が行います。管理者に誤りの内容を連絡してください</li>
          <li>給与申告の修正は「修正依頼を送る」ボタンから依頼できます（「給与申告をする」のヘルプを参照）</li>
        </ul>

        <h4>画面の表示がおかしい</h4>
        <ul>
          <li>ブラウザの更新（F5キーまたは「⟳」ボタン）を試してください</li>
          <li>改善しない場合は管理者に連絡してください</li>
        </ul>
      </>
    ),
  },
  {
    id: 'faq',
    title: 'よくある質問',
    content: (
      <>
        <dl className="help-faq">
          <dt>Q. 日報を書き忘れた日の日報を後から追加できますか？</dt>
          <dd>A. はい。日報入力フォームで日付を変更して過去分を入力できます。</dd>

          <dt>Q. 日報の同じ日付に2件入力できますか？</dt>
          <dd>A. 同じ日付の日報が既にある場合、「日報を上書きしますか？」という確認ダイアログが表示されます。上書きすると既存の日報が更新されます（1日1件）。</dd>

          <dt>Q. シフト希望を出し忘れた場合はどうすればいいですか？</dt>
          <dd>A. 締め切り前であれば入力できます。締め切り後の場合は管理者に直接連絡してください。</dd>

          <dt>Q. 給与申告を提出した後で間違いに気づきました。</dt>
          <dd>A. 提出後は自分では直接修正できません。給与申告ページの「修正依頼を送る」ボタンから管理者に修正依頼を送ってください。</dd>

          <dt>Q. 勤怠入力の「フルタイム（日給）」と「時間勤務・時短勤務」はどう使い分けますか？</dt>
          <dd>A. 通常の1日勤務は「フルタイム（日給）」を選んでください。早退・時短・一部時間のみの勤務は「時間勤務・時短勤務」を選んで勤務時間（分）を入力してください。</dd>

          <dt>Q. スマートフォンから使えますか？</dt>
          <dd>A. はい、スマートフォンのブラウザからご利用いただけます。左上の「☰」（ハンバーガーメニュー）から各ページへ移動できます。</dd>

          <dt>Q. テーマの見た目を変えられますか？</dt>
          <dd>A. はい、右上のボタン（PC）から「ライト/ダーク」の切り替えや、「漫画モード」への切り替えができます。</dd>
        </dl>
      </>
    ),
  },
]

// ----------------------------------------------------------------
// ヘルプページ本体
// ----------------------------------------------------------------
export default function AssistantHelp() {
  const [activeId, setActiveId] = useState<string>(sections[0].id)

  const activeSection = sections.find((s) => s.id === activeId) ?? sections[0]
  const activeIdx = sections.findIndex((s) => s.id === activeId)

  return (
    <div className="help-layout">
      {/* 目次サイドバー */}
      <nav className="help-toc">
        <div className="help-toc-title">目次</div>
        {sections.map((s) => (
          <button
            key={s.id}
            className={`help-toc-item${activeId === s.id ? ' active' : ''}`}
            onClick={() => setActiveId(s.id)}
          >
            {s.title}
          </button>
        ))}
      </nav>

      {/* 本文 */}
      <article className="help-content">
        <h2 className="help-section-title">{activeSection.title}</h2>
        <div className="help-body">
          {activeSection.content}
        </div>

        {/* 前後ナビ */}
        <div className="help-nav-btns">
          {activeIdx > 0 && (
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setActiveId(sections[activeIdx - 1].id)}
            >
              ← 前へ
            </button>
          )}
          {activeIdx < sections.length - 1 && (
            <button
              className="btn btn-primary btn-sm"
              style={{ marginLeft: 'auto' }}
              onClick={() => setActiveId(sections[activeIdx + 1].id)}
            >
              次へ →
            </button>
          )}
        </div>
      </article>
    </div>
  )
}
